/**
 * Everyday units tab — PLT-4915.
 *
 * (Renamed from "Real-world units" in Janne review v3 — "everyday units"
 * reads more conversationally; the tab still does the same job: convert
 * the dollars-per-year figure into intuitive physical equivalents.)
 *
 * Lists physical-equivalent figures (DALY, GHG-tons, mosquito nets,
 * jobs, …) for both valences of the selected impact category. Data
 * comes from two pure helpers in `src/common/model/output/scores/
 * physical-intensity/` plus a small inlined GHG helper — no
 * react-query, no hooks.
 *
 * Design notes (May 2026 polish pass v2, Janne review):
 *   - Per-valence section header ("Equivalents of <entity>'s positive
 *     <category> impact") opens each section. v6 removed the right-
 *     aligned c/$ figure that v5 had: Janne's feedback was that "Total
 *     impact + c/$ figure" on the same line as items that are
 *     physical-equivalent counts read as misleading — the title now
 *     names exactly what the rows below it are (equivalents). The c/$
 *     totals are still visible in the panel's stat row above the tabs
 *     and on the Positive/Negative contributors tab headers.
 *   - Per equivalent: 32px valence-tinted icon + quantity line + hedge
 *     sentence directly under it. The hedge does ~70% of the visual
 *     work in mitigating the "Siemens makes mosquito nets" misread.
 *   - No italic / dashed-divider footnote at the bottom — the hedge
 *     sentence on every figure already carries the disclaimer, and
 *     italics are off the table per Janne's review v2.
 *   - GHG categories (Environment / GHG emissions) now use a small
 *     inlined GHG-specific equivalent set (tons of GHG emissions,
 *     U.S. household-year equivalents) — the legacy modal's behaviour,
 *     ported across so this tab doesn't fall through to "no real-
 *     world units" for the most-asked-about impact.
 *   - Body / hedge / "per year" text use `gray-700` rather than the
 *     near-illegible `gray-500` the v1 cut shipped with.
 */
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { dollarsToPhysicalComparisons } from '@upright/common/model/output/scores/physical-intensity';
import { Valence } from '@upright/common/model/types';
import { formatNumberEfficiently, NumberType } from '@upright/ui/common/generic/utilities/format-numbers-efficiently';
import { ComponentType, SVGProps } from 'react';
import styled from 'styled-components';
import { DrillInSectionHeader } from '../components/section-header';
import { NetImpactDrillInPayload } from '../state';

/* Icon imports — reused from the legacy `generic-products.tsx` so the
 * label → icon mapping stays in sync with the rest of the platform.
 * Same `as any` cast as the legacy file because the streamline SVG
 * loader types aren't perfectly inferable.
 *
 * eslint-disable @typescript-eslint/no-unsafe-assignment */
import HeartRateDownIcon from '@streamlinehq/streamlinehq/img/manual/monitor-heart-rate-down.svg';
import HeartRateUpIcon from '@streamlinehq/streamlinehq/img/streamline-light/health-beauty/monitoring/monitor-heart-rate-up.svg';
import TentIcon from '@streamlinehq/streamlinehq/img/streamline-light/outdoors-recreation/camping/camping-tent-sleep.svg';
import PillsIcon from '@upright/ui/icons/img/manual/contraceptive-pills-sperm-2.svg';
import CounselingIcon from '@upright/ui/icons/img/manual/counseling-1.svg';
import EngineerIcon from '@upright/ui/icons/img/manual/design-person-pencil.svg';
import DishwasherIcon from '@upright/ui/icons/img/manual/dishwasher.svg';
import FakeIcon from '@upright/ui/icons/img/manual/fake-photo.svg';
import WaterIcon from '@upright/ui/icons/img/manual/wave.svg';
import NerdIcon from '@upright/ui/icons/img/streamline-light/avatars/geometric-women-people/people-woman-graduate.svg';
import ScientistIcon from '@upright/ui/icons/img/streamline-light/avatars/geometric-women-professions/avatar-scientist-woman.svg';
import DoctorIcon from '@upright/ui/icons/img/streamline-light/avatars/geometric-women-professions/professions-woman-doctor-2.svg';
import JobsIcon from '@upright/ui/icons/img/streamline-light/avatars/natural-men-professions/professions-man-welder.svg';
import BuildingIcon from '@upright/ui/icons/img/streamline-light/building-construction/buildings/buildings.svg';
import HouseIcon from '@upright/ui/icons/img/streamline-light/building-construction/houses/house-1.svg';
import ShirtIcon from '@upright/ui/icons/img/streamline-light/clothes-footwear-accessories/shirts-sweaters-top/shirt-female.svg';
import LaptopIcon from '@upright/ui/icons/img/streamline-light/computers-devices-electronics/laptop-computers/laptop.svg';
import CutTreeIcon from '@upright/ui/icons/img/streamline-light/ecology/deforestation/deforestation-cut-tree.svg';
import SoilIcon from '@upright/ui/icons/img/streamline-light/ecology/plant-growth-organic-fertilizer/organic-plant-root.svg';
import Co2Icon from '@upright/ui/icons/img/streamline-light/ecology/pollution/pollution-co-2.svg';
import WaterPollutionIcon from '@upright/ui/icons/img/streamline-light/ecology/pollution/pollution-faucet-water.svg';
import PlasticBagIcon from '@upright/ui/icons/img/streamline-light/ecology/recycling/plastic-bag.svg';
import RollerCoasterIcon from '@upright/ui/icons/img/streamline-light/entertainment-events-hobbies/amusement-parks/amusement-park-rollercoaster.svg';
import GamblingIcon from '@upright/ui/icons/img/streamline-light/entertainment-events-hobbies/card-games/card-game-dice.svg';
import MealIcon from '@upright/ui/icons/img/streamline-light/food-drinks/asian-food/asian-food-noodle-ramen-bowl.svg';
import ChocolateBarIcon from '@upright/ui/icons/img/streamline-light/food-drinks/cakes-candies/chocolate-bar.svg';
import SpiritsIcon from '@upright/ui/icons/img/streamline-light/food-drinks/champagne-spirits/spirits-glass.svg';
import BananaIcon from '@upright/ui/icons/img/streamline-light/food-drinks/fruits/fruit-banana.svg';
import AsianInteriorIcon from '@upright/ui/icons/img/streamline-light/furnitures-decoration/asian-interiors/asian-interior-windows.svg';
import TranslateIcon from '@upright/ui/icons/img/streamline-light/interface-essential/text-options/translate.svg';
import TreeIcon from '@upright/ui/icons/img/streamline-light/nature-farming/trees/tree-2.svg';
import HippoIcon from '@upright/ui/icons/img/streamline-light/pets-animals/elephants-rhinoceros/hippo.svg';
import PhoneCallIcon from '@upright/ui/icons/img/streamline-light/phones-mobile-devices/phone-actions/phone-actions-smile.svg';
import LessonsIcon from '@upright/ui/icons/img/streamline-light/school-learning/school/school-board-chemistry.svg';
import SchoolIcon from '@upright/ui/icons/img/streamline-light/school-learning/school/school-teacher-maths.svg';
import MetalIcon from '@upright/ui/icons/img/streamline-light/tools-constructions/materials/metal-sheet.svg';
import RailRoadIcon from '@upright/ui/icons/img/streamline-light/transportation/railroad/railroad-fast-train.svg';
import RoadSign from '@upright/ui/icons/img/streamline-light/transportation/roads-lights/road-sign.svg';
import EvacuationCenterIcon from '@upright/ui/icons/img/streamline-light/travel/refugees-immigration/evacuation-center.svg';
import TVIcon from '@upright/ui/icons/img/streamline-light/users/geomertic-close-up-single-user-woman/single-woman-vintage-tv.svg';
import SmokingIcon from '@upright/ui/icons/img/streamline-light/wayfinding/allowances/allowances-smoking.svg';
import CareIcon from '@upright/ui/icons/img/streamline-light/wayfinding/disability/disability-cane.svg';
import DiamondIcon from '@upright/ui/icons/img/streamline-regular/money-payments-finance/wealth/diamond.svg';

interface Props {
  payload: NetImpactDrillInPayload;
  impactLabel: string;
  entityName: string;
}

interface ValueRow {
  /** The numeric quantity (e.g. 266_000_000). */
  value: number;
  /** Full label, used in the hedge sentence. */
  label: string;
  /** Short label, used in the quantity line. */
  shortLabel?: string;
  /** Icon to render in the 32px tinted square. */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: ComponentType<SVGProps<SVGSVGElement>> | any | undefined;
}

export function EverydayUnitsTab({ payload, impactLabel, entityName }: Props) {
  // The everyday-units tab is per impact category — the panel hides it
  // at dimension grain (PLT-4940), so this guard is defensive only.
  if (payload.target.grain !== 'impact') return null;
  const impactCode = payload.target.impactCode;
  const positiveRows = collectRows(impactCode, payload.positiveDollarsPerYear, Valence.POSITIVE);
  const negativeRows = collectRows(impactCode, payload.negativeDollarsPerYear, Valence.NEGATIVE);

  if (positiveRows.length === 0 && negativeRows.length === 0) {
    return (
      <Empty>
        <EmptyTitle>No everyday equivalents available</EmptyTitle>
        <EmptyBody>
          We don't yet publish per-category everyday equivalents for {impactLabel.toLowerCase()}. The cents-per-dollar
          figures above and the contributors tabs are the best sources for this category.
        </EmptyBody>
      </Empty>
    );
  }

  return (
    <Root>
      {positiveRows.length > 0 && (
        <ValenceSection>
          <DrillInSectionHeader title={`Equivalents of ${entityName}'s positive ${impactLabel.toLowerCase()} impact`} />
          <FigureList>
            {positiveRows.map((row, i) => (
              <FigureRow
                key={`pos-${row.label}-${i}`}
                row={row}
                valence={Valence.POSITIVE}
                impactLabel={impactLabel}
                entityName={entityName}
              />
            ))}
          </FigureList>
        </ValenceSection>
      )}
      {negativeRows.length > 0 && (
        <ValenceSection>
          <DrillInSectionHeader title={`Equivalents of ${entityName}'s negative ${impactLabel.toLowerCase()} impact`} />
          <FigureList>
            {negativeRows.map((row, i) => (
              <FigureRow
                key={`neg-${row.label}-${i}`}
                row={row}
                valence={Valence.NEGATIVE}
                impactLabel={impactLabel}
                entityName={entityName}
              />
            ))}
          </FigureList>
        </ValenceSection>
      )}
    </Root>
  );
}

function FigureRow({
  row,
  valence,
  impactLabel,
  entityName,
}: {
  row: ValueRow;
  valence: Valence;
  impactLabel: string;
  entityName: string;
}) {
  const formatted = formatNumberEfficiently(row.value, NumberType.GENERIC, 3);
  const quantityLabel = row.shortLabel || row.label;
  const valenceWord = valence === Valence.POSITIVE ? 'positive' : 'negative';
  // The hedge sentence is the part Juho's note explicitly protects —
  // says "equivalent" twice (in different phrasing), names the impact
  // category, and ends with "not a claim about what <entity> actually
  // produces". Lowering impactLabel because mid-sentence.
  const hedge = `${entityName}'s ${valenceWord} ${impactLabel.toLowerCase()} impact has an equivalent magnitude to ${formatted} ${row.label} per year — not a claim about what ${entityName} produces.`;
  const Icon = row.icon;
  return (
    <FigureRowRoot>
      <IconSquare $valence={valence}>
        {Icon ? (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          <Icon />
        ) : (
          <IconFallback aria-hidden="true">≈</IconFallback>
        )}
      </IconSquare>
      <FigureBody>
        <Quantity>
          ≈ <QuantityValue>{formatted}</QuantityValue> {quantityLabel} <PerYear>per year</PerYear>
        </Quantity>
        <Hedge>{hedge}</Hedge>
      </FigureBody>
    </FigureRowRoot>
  );
}

/**
 * Collect rows for one valence. Branches on impact category:
 *   - GHG_EMISSIONS: bespoke 2-row set (tons of GHG, U.S. households)
 *     — inlined from the legacy `useGhgComparisons` hook so we don't
 *     depend on the `NetImpactLensContext` (this panel is mounted at
 *     the app root, outside any lens context).
 *   - everything else: the generic physical-intensity table.
 */
function collectRows(impact: ImpactCategoryId, dollarsPerYear: number | null, valence: Valence): ValueRow[] {
  if (!dollarsPerYear || dollarsPerYear <= 0) return [];

  if (impact === ImpactCategoryId.GHG_EMISSIONS) {
    return ghgRows(dollarsPerYear);
  }

  const items = dollarsToPhysicalComparisons(dollarsPerYear, impact, valence);
  return items.map(item => ({
    value: item.value,
    label: item.label,
    shortLabel: item.shortLabel,
    icon: getUnitIcon(item.label, valence),
  }));
}

/**
 * GHG equivalents — inlined from `useGhgComparisons` in
 * `real-world-units-comparisons/ghg.tsx`. Same constants (carbon
 * price = $417 / t, U.S. household average = 7.5 t / yr) and same
 * two-figure shape (tons of GHG emissions, then U.S. households).
 */
function ghgRows(dollarsPerYear: number): ValueRow[] {
  const carbonPrice = 417;
  const householdAverageGhgTons = 7.5;
  const tonsPerYear = dollarsPerYear / carbonPrice;
  const householdProductionPerYear = tonsPerYear / householdAverageGhgTons;
  return [
    {
      value: tonsPerYear,
      label: 'tons of GHG emissions',
      icon: Co2Icon,
    },
    {
      value: householdProductionPerYear,
      label: 'average annual emissions of U.S. households',
      shortLabel: 'U.S. households',
      icon: HouseIcon,
    },
  ];
}

/* `formatCentsPerDollar` removed in Janne review v6 — the section
   header on this tab no longer carries a per-valence c/$ figure. The
   header now states what the rows below are ("Equivalents of …"); the
   c/$ totals already appear in the panel's stat row above the tab
   strip and on the contributors tabs, so repeating them here was
   redundant and (per Janne) made the title misleading by implying the
   rows below it were the c/$ value rather than physical equivalents. */

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-member-access */
function getUnitIcon(label: string, valence: Valence): any {
  const entry = unitIcons[label];
  if (entry && typeof entry === 'object' && 'POSITIVE' in entry) {
    return entry[valence === Valence.POSITIVE ? 'POSITIVE' : 'NEGATIVE'];
  }
  return entry;
}

/**
 * Mirrors the legacy `unitIcons` map in
 * `generic-products.tsx`. Kept as a separate constant here to avoid a
 * runtime dependency on `NetImpactLensContext` (the legacy file's hook
 * chain pulls context from a provider this side panel doesn't have).
 */
const unitIcons: Record<string, any> = {
  'direct or indirect jobs': JobsIcon,
  'days of primary education': SchoolIcon,
  'person-years of primary health care': DoctorIcon,
  'long-haul train rides': RailRoadIcon,
  'five-storey apartment buildings': BuildingIcon,
  'days of elderly care': CareIcon,
  'half carat natural diamonds': DiamondIcon,
  'hours of interpretation services': TranslateIcon,
  'hours of cultural education': AsianInteriorIcon,
  'articles of fast fashion clothing': ShirtIcon,
  'packages of birth control pills': PillsIcon,
  'emergency shelter tents': EvacuationCenterIcon,
  'personal computers': LaptopIcon,
  'phase 3 trials of cardiovascular medicine': ScientistIcon,
  'hours of mechanical prototyping services': EngineerIcon,
  'online fake news articles that reach 5M readers': FakeIcon,
  'days of BBC television news programming': TVIcon,
  'science lessons': LessonsIcon,
  'person-years of scarce, highly educated workers': NerdIcon,
  'disability-adjusted life years (DALY)': { POSITIVE: HeartRateUpIcon, NEGATIVE: HeartRateDownIcon },
  'mosquito nets': TentIcon,
  cigarettes: SmokingIcon,
  'psychotherapy sessions': CounselingIcon,
  bananas: BananaIcon,
  'ready meals': MealIcon,
  'online gambling sessions': GamblingIcon,
  'bottles of vodka': SpiritsIcon,
  'phone calls': PhoneCallIcon,
  'hours of couples therapy': CounselingIcon,
  'hours of meaningless social media scrolling': undefined,
  'roller coaster rides': RollerCoasterIcon,
  'chocolate bars': ChocolateBarIcon,
  'cubic meters of spilled oil': WaterPollutionIcon,
  'cubic meters of remediated soil': SoilIcon,
  'dishwasher cycles': DishwasherIcon,
  'kilograms of germanium': MetalIcon,
  'cubic meters of treated wastewater': WaterIcon,
  'square meters of felled forest': CutTreeIcon,
  'highway kilometers': RoadSign,
  'square meters of planted forest': TreeIcon,
  'hours of biodiversity conservation services': HippoIcon,
  'plastic bags': PlasticBagIcon,
  'tons of GHG emissions': Co2Icon,
  'average annual emissions of U.S. households': HouseIcon,
};
/* eslint-enable @typescript-eslint/no-explicit-any */

/* ─── styled ─────────────────────────────────────────────────────────── */

const Root = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__xl);
`;

const ValenceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__md);
`;

/* Per-valence section header is now provided by `DrillInSectionHeader`
   (../components/section-header). The same component is reused by the
   Positive/Negative contributors tabs so the three tabs share a single
   visual treatment for "this is the {entity}'s {valence} {category}
   impact" + the c/$ figure on the right. */

const FigureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space__lg);
`;

const FigureRowRoot = styled.div`
  display: grid;
  grid-template-columns: 32px minmax(0, 1fr);
  gap: var(--space__md);
  align-items: flex-start;
`;

const IconSquare = styled.div<{ $valence: Valence }>`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius__sm);
  background: ${p => (p.$valence === Valence.POSITIVE ? 'var(--color__teal-100)' : 'var(--color__purple-100)')};
  color: ${p => (p.$valence === Valence.POSITIVE ? 'var(--color__teal-700)' : 'var(--color__purple-700)')};
  flex-shrink: 0;
  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
    stroke: currentColor;
  }
`;

const IconFallback = styled.span`
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__bold);
`;

const FigureBody = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Quantity = styled.div`
  font-size: var(--font-size__md);
  color: var(--color__gray-900);
  line-height: 1.3;
  word-break: break-word;
`;

const QuantityValue = styled.span`
  font-weight: var(--font-weight__bold);
  font-variant-numeric: tabular-nums;
`;

const PerYear = styled.span`
  color: var(--color__gray-700);
  font-weight: var(--font-weight__regular);
`;

const Hedge = styled.div`
  font-size: var(--font-size__sm);
  color: var(--color__gray-700);
  line-height: 1.5;
`;

const Empty = styled.div`
  padding: var(--space__lg) 0;
`;

const EmptyTitle = styled.div`
  font-size: var(--font-size__md);
  font-weight: var(--font-weight__bold);
  color: var(--color__gray-900);
  margin-bottom: 4px;
`;

const EmptyBody = styled.div`
  font-size: var(--font-size__sm);
  color: var(--color__gray-700);
  line-height: 1.5;
`;
